import React, { useState } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Alert,
  AlertDescription,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import RoleManagementTab from './RoleManagementTab';
import PermissionManagementTab from './PermissionManagementTab';

type RBACTab = 'role-management' | 'permission-management';

const TABS: { id: RBACTab; label: string }[] = [
  { id: 'role-management', label: 'Role Management' },
  { id: 'permission-management', label: 'Permission Management' },
];

const RolesManager: React.FC = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );

  const [activeTab, setActiveTab] = useState<RBACTab>('role-management');

  if (!hasPermission('admin:super-user')) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You do not have permission to view this page. Please contact your
            administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Roles & Permissions</h1>
        <p className='text-muted-foreground'>
          Manage roles and permissions for users in your agency.
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={value => setActiveTab(value as RBACTab)}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        <TabsList className='flex h-auto min-h-8 w-full gap-0.5 rounded-md border border-border bg-muted/40 p-1'>
          {TABS.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className='flex-1 px-2 py-1.5 text-sm font-medium data-[state=active]:shadow-none'
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value='role-management' className='m-0'>
          <RoleManagementTab />
        </TabsContent>

        <TabsContent value='permission-management' className='m-0'>
          <PermissionManagementTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RolesManager;
